/**
 * RecipeServingsTable — the loggable-unit table for an unsaved recipe.
 *
 * Every recipe exposes a fixed gram-weight conversion table: each row maps a
 * named unit to "how many grams one of it weighs". Three backend-computed rows
 * are always present (Full recipe, the selected serving type, and a raw gram),
 * so they're locked here; clinicians may add extra logging units (e.g. "1 oz")
 * on top.
 *
 * The `mode` prop flips only the displayed value column — the stored fact is
 * always grams-per-unit:
 *   - "weight"  → "Grams per serving"   = the unit's gram weight as-is.
 *   - "serving" → "Servings per recipe", provided by the backend for locked
 *                 core rows.
 *
 * Serving units come from the shared <ServingUnitCombobox>, which reads the
 * global DB list and creates a missing unit inline.
 */
import { useMemo, useState } from "react";
import { ExternalLink, Lock, Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RequiredMark } from "@/components/settings/nutrition-shared";
import { ServingUnitCombobox } from "@/components/settings/ServingUnitCombobox";
import {
  buildDescription,
  nameUnitFromDescription,
  previewMultiplierFor,
  SERVING_DESCRIPTION_HELPER,
  SERVING_DESCRIPTION_PLACEHOLDER,
  SERVING_ML_WEIGHT_HELPER,
  SERVING_MULTIPLIER_HELPER,
  SERVING_MULTIPLIER_PLACEHOLDER,
} from "@/components/settings/serving-form-shared";
import { InfoPopover } from "@/components/settings/InfoPopover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

/** Whether the value column reads as grams or as servings-per-recipe. */
export type ServingMode = "serving" | "weight";

/**
 * A loggable unit on a recipe.
 *
 * `kind` distinguishes the three derived rows the parent locks (full recipe,
 * the picked serving type, and a raw gram) from manual rows the user adds for
 * logging convenience. `weightG` is always "grams of ONE of this unit".
 */
export type RecipeServingKind =
  | "core-full"
  | "core-serving"
  | "core-gram"
  | "manual"
  | "food";

export interface RecipeServing {
  id: string;
  kind: RecipeServingKind;
  servingUnitCode: number;
  servingUnitName: string;
  /** Display name, e.g. "Cup (~150g)". Absent on rows from the old backend. */
  description?: string;
  weightG: string;
  servingsPerRecipe?: string;
}

/**
 * Smallest weight the backend accepts on a recipe serving row (columbus#406
 * raised the floor from 0 to 0.1 g). Enforced here so a 0.05 g row is caught in
 * this dialog rather than 400ing later, when the whole recipe save fails and
 * nothing points at the row that caused it.
 */
const MIN_SERVING_WEIGHT_G = 0.1;

/**
 * Units the backend creates itself on every recipe (Full recipe, Serve, gm).
 * A custom row can't reuse one: the built-in wins and the row would be dropped
 * at first publish. Matched case-insensitively on the trimmed name.
 */
const BUILTIN_SERVING_UNIT_NAMES = ["full recipe", "serve", "gm"];
const isBuiltinUnitName = (name: string): boolean =>
  BUILTIN_SERVING_UNIT_NAMES.includes(name.trim().toLowerCase());

/** Trim trailing zeros for display without forcing a fixed precision. */
const fmt = (n: number): string => {
  if (!Number.isFinite(n) || n <= 0) return "—";
  const r = Math.round(n * 100) / 100;
  return r.toString();
};

interface ServingDialogState {
  open: boolean;
  editingId: string | null;
  servingUnitCode: number | null;
  servingUnitName: string;
  weightG: string;
  nameUnit: "g" | "ml";
}

const emptyDialogState: ServingDialogState = {
  open: false,
  editingId: null,
  servingUnitCode: null,
  servingUnitName: "",
  weightG: "",
  nameUnit: "g",
};

interface RecipeServingsTableProps {
  /** "weight" → grams column; "serving" → servings-per-recipe column. */
  mode: ServingMode;
  /** The three derived rows (Full recipe / serving type / gram). Read-only. */
  coreRows: RecipeServing[];
  /** Manual extra units the user adds. */
  value: RecipeServing[];
  onChange: (next: RecipeServing[]) => void;
  /**
   * Servings added on the recipe's food page. The recipe and its food share one
   * food code and one servings list, so they show here too — read-only, since
   * the food page owns them.
   */
  foodRows?: RecipeServing[];
  /**
   * True once the recipe has been published. The servings then belong to the
   * food: the table lists them read-only (core + authored + food-page rows,
   * de-duplicated by unit) and offers no add, edit or remove.
   */
  readOnly?: boolean;
  /** Opens the food page. Omit to hide the "Manage servings" button. */
  onManageServings?: () => void;
}

export const RecipeServingsTable = ({
  mode,
  coreRows,
  value,
  onChange,
  foodRows = [],
  readOnly = false,
  onManageServings,
}: RecipeServingsTableProps) => {
  const [dialog, setDialog] = useState<ServingDialogState>(emptyDialogState);

  const valueHeader =
    mode === "weight" ? "Grams per serving" : "Servings per recipe";

  // The full recipe weight lives on the backend-computed "core-full" row. Use
  // it to derive "servings per recipe" for manual rows (the backend only sends
  // that figure for core rows), so user-added units don't read a permanent "—".
  const fullWeightG = useMemo(() => {
    const core = coreRows.find((r) => r.kind === "core-full");
    const g = core ? parseFloat(core.weightG) : NaN;
    return Number.isFinite(g) && g > 0 ? g : null;
  }, [coreRows]);

  /** The number shown in the value column for a row of `grams` per unit. */
  const displayValue = (row: RecipeServing, grams: number): string => {
    if (mode === "weight") return fmt(grams);
    // Core rows carry the backend-computed figure verbatim.
    if (row.servingsPerRecipe) return row.servingsPerRecipe;
    // Manual rows: how many of this unit fit in the full recipe.
    if (fullWeightG && Number.isFinite(grams) && grams > 0) {
      return fmt(fullWeightG / grams);
    }
    return "—";
  };

  // Published: one list per unit. Core rows win; the old backend returns the
  // recipe-authored units in `value` and the rest in `foodRows`, the new one
  // returns `value` empty. De-duplicating by unit code makes both render alike.
  const publishedExtraRows = useMemo(() => {
    const seen = new Set(coreRows.map((r) => r.servingUnitCode));
    return [...value, ...foodRows].filter((r) => {
      if (seen.has(r.servingUnitCode)) return false;
      seen.add(r.servingUnitCode);
      return true;
    });
  }, [coreRows, value, foodRows]);

  const openCreateDialog = () => setDialog({ ...emptyDialogState, open: true });

  const openEditDialog = (serving: RecipeServing) =>
    setDialog({
      open: true,
      editingId: serving.id,
      servingUnitCode: serving.servingUnitCode,
      servingUnitName: serving.servingUnitName,
      weightG: serving.weightG,
      nameUnit: nameUnitFromDescription(serving.description),
    });

  const closeDialog = () => setDialog(emptyDialogState);

  // Guard against two rows claiming the same unit — the resulting serving_rows
  // payload would be ambiguous ("how many grams is unit X?"). Excludes the row
  // being edited; checks both existing manual rows and the locked core rows.
  const isDuplicateUnit =
    dialog.servingUnitCode !== null &&
    [...coreRows, ...value].some(
      (r) =>
        r.id !== dialog.editingId &&
        r.servingUnitCode === dialog.servingUnitCode
    );

  // Blank reads as "not filled in yet" and only disables the button; a typed
  // value that is out of range gets a message, so the user is told which of the
  // two they are looking at.
  const hasWeight = dialog.weightG.trim().length > 0;
  const parsedWeight = parseFloat(dialog.weightG);
  const weightError =
    hasWeight &&
    (!Number.isFinite(parsedWeight) || parsedWeight < MIN_SERVING_WEIGHT_G)
      ? `Enter a weight of at least ${MIN_SERVING_WEIGHT_G} g.`
      : null;

  const previewDescription = buildDescription(
    dialog.servingUnitName || undefined,
    dialog.weightG,
    dialog.nameUnit
  );
  const previewMultiplier = previewMultiplierFor(dialog.weightG);

  const isBuiltinUnit = isBuiltinUnitName(dialog.servingUnitName);

  const isValid =
    dialog.servingUnitCode !== null &&
    hasWeight &&
    !weightError &&
    !isDuplicateUnit &&
    !isBuiltinUnit;

  const handleSave = () => {
    if (!isValid || dialog.servingUnitCode === null) return;
    const next: RecipeServing = {
      id: dialog.editingId ?? crypto.randomUUID(),
      kind: "manual",
      servingUnitCode: dialog.servingUnitCode,
      servingUnitName: dialog.servingUnitName,
      weightG: dialog.weightG.trim(),
      description: previewDescription,
    };
    onChange(
      dialog.editingId
        ? value.map((r) => (r.id === dialog.editingId ? next : r))
        : [...value, next]
    );
    closeDialog();
  };

  const handleRemove = (id: string) =>
    onChange(value.filter((r) => r.id !== id));

  const renderRow = (row: RecipeServing, locked: boolean) => {
    const grams = parseFloat(row.weightG);
    const qtyLabel =
      row.kind === "core-full"
        ? row.servingUnitName
        : `1 ${row.servingUnitName}`;
    return (
      <TableRow key={row.id}>
        <TableCell className="z-body-md">{qtyLabel}</TableCell>
        <TableCell className="z-body-md text-muted-foreground">
          {row.description || "–"}
        </TableCell>
        <TableCell className="text-right z-numeric">
          {displayValue(row, grams)}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-1">
            {locked ? (
              <Lock
                className="h-3.5 w-3.5 text-muted-foreground"
                aria-label="Locked"
              />
            ) : (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => openEditDialog(row)}
                  aria-label="Edit serving unit"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => handleRemove(row.id)}
                  aria-label="Remove serving unit"
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 min-w-0">
          {readOnly ? (
            <span className="z-label">Servings</span>
          ) : (
            <p className="z-caption text-muted-foreground">
              Add servings so a clinician or admin can log this in their own
              measure.
            </p>
          )}
          <InfoPopover
            title="Servings"
            lines={[
              ...(readOnly
                ? [
                    "Click Manage servings to add a serving or switch one off on the food page.",
                    "To change a weight, switch the serving off and add a new one.",
                  ]
                : [
                    "Click Add Serving, pick a unit and enter its weight.",
                    "The first three rows are automatic and can't be edited.",
                  ]),
            ]}
          />
        </div>
        {readOnly ? (
          onManageServings && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onManageServings}
            >
              <ExternalLink className="size-4" />
              Manage servings
            </Button>
          )
        ) : (
          <Button size="sm" onClick={openCreateDialog}>
            <Plus className="size-4" />
            Add Serving
          </Button>
        )}
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="z-eyebrow text-muted-foreground">
                Serving
              </TableHead>
              <TableHead className="z-eyebrow text-muted-foreground">
                Description
              </TableHead>
              <TableHead className="z-eyebrow text-muted-foreground text-right">
                {valueHeader}
              </TableHead>
              <TableHead className="z-eyebrow text-muted-foreground text-right w-[90px]">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {coreRows.length === 0 && (readOnly || value.length === 0) && (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={4}
                  className="h-20 text-center z-caption text-muted-foreground"
                >
                  Add ingredients and save the recipe to see conversions, or add
                  a serving unit above.
                </TableCell>
              </TableRow>
            )}
            {coreRows.map((row) => renderRow(row, true))}

            {readOnly && publishedExtraRows.length > 0 && (
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableCell
                  colSpan={4}
                  className="z-eyebrow text-muted-foreground uppercase tracking-wide py-2"
                >
                  Other servings on this food
                </TableCell>
              </TableRow>
            )}
            {readOnly && publishedExtraRows.map((row) => renderRow(row, true))}

            {!readOnly && value.length > 0 && (
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableCell
                  colSpan={4}
                  className="z-eyebrow text-muted-foreground uppercase tracking-wide py-2"
                >
                  Additional servings
                </TableCell>
              </TableRow>
            )}
            {!readOnly && value.map((row) => renderRow(row, false))}

            {!readOnly && foodRows.length > 0 && (
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableCell
                  colSpan={4}
                  className="z-eyebrow text-muted-foreground uppercase tracking-wide py-2"
                >
                  Added on the food page
                </TableCell>
              </TableRow>
            )}
            {!readOnly && foodRows.map((row) => renderRow(row, true))}
          </TableBody>
        </Table>
      </div>

      <Dialog
        open={dialog.open}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>
              {dialog.editingId ? "Edit serving" : "Add serving"}
            </DialogTitle>
          </DialogHeader>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="z-secondary">
                  Serving unit <RequiredMark />
                </Label>
                <ServingUnitCombobox
                  valueCode={dialog.servingUnitCode}
                  valueName={dialog.servingUnitName}
                  onSelect={(code, name) =>
                    setDialog((d) => ({
                      ...d,
                      servingUnitCode: code,
                      servingUnitName: name,
                    }))
                  }
                />
                {isDuplicateUnit && (
                  <p className="z-caption text-destructive">
                    “{dialog.servingUnitName}” is already used by another
                    serving. Pick a different unit.
                  </p>
                )}
                {isBuiltinUnit && (
                  <p className="z-caption text-destructive">
                    “{dialog.servingUnitName}” is a built-in serving that
                    follows the recipe. Pick a different unit.
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="z-secondary">Description</Label>
                <Input
                  readOnly
                  disabled
                  value={previewDescription}
                  placeholder={SERVING_DESCRIPTION_PLACEHOLDER}
                />
                <p className="z-caption text-muted-foreground">
                  {SERVING_DESCRIPTION_HELPER}
                </p>
              </div>

              <div className="flex items-center justify-between gap-2">
                <span className="z-label">Show the name in</span>
                <ToggleGroup
                  type="single"
                  variant="outline"
                  size="sm"
                  value={dialog.nameUnit}
                  onValueChange={(value) => {
                    if (value === "g" || value === "ml") {
                      setDialog((d) => ({ ...d, nameUnit: value }));
                    }
                  }}
                >
                  <ToggleGroupItem value="g" aria-label="Grams">
                    g
                  </ToggleGroupItem>
                  <ToggleGroupItem value="ml" aria-label="Millilitres">
                    ml
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>

              <div className="space-y-1.5">
                <Label className="z-secondary">
                  Weight (g) <RequiredMark />
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min={MIN_SERVING_WEIGHT_G}
                  placeholder="e.g., 150.0"
                  value={dialog.weightG}
                  onChange={(e) =>
                    setDialog((d) => ({ ...d, weightG: e.target.value }))
                  }
                />
                {dialog.nameUnit === "ml" && (
                  <p className="z-caption text-muted-foreground">
                    {SERVING_ML_WEIGHT_HELPER}
                  </p>
                )}
                {weightError ? (
                  <p className="z-caption text-destructive">{weightError}</p>
                ) : (
                  <p className="z-caption text-muted-foreground">
                    Minimum {MIN_SERVING_WEIGHT_G} g.
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="z-secondary">Multiplier</Label>
                <Input
                  readOnly
                  disabled
                  value={previewMultiplier}
                  placeholder={SERVING_MULTIPLIER_PLACEHOLDER}
                />
                <p className="z-caption text-muted-foreground">
                  {SERVING_MULTIPLIER_HELPER}
                  {previewMultiplier
                    ? " This is a preview; it is set when you save."
                    : ""}
                </p>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={!isValid}>
                {dialog.editingId ? "Save changes" : "Add serving"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecipeServingsTable;
